import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { directSrcFrom, withSurveyCapture } from "./embed";
import { ArrowLeft, ArrowUpRight, CheckCircle2, ClipboardList, ExternalLink, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { IMG } from "@/data/vomarcData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeLabel = { native: "Interactive form", link: "External link", html: "Live preview" };

function SurveyHeader() {
  return (
    <header className="survey-header" data-testid="survey-page-header">
      <Link to="/" className="survey-brand" data-testid="survey-brand-link">
        <img src={IMG.logo} alt="VOMARC" width="40" height="40" />
        <span>VOMARC</span>
      </Link>
      <Link to="/" className="ghost-cta-dark survey-back" data-testid="survey-back-home"><ArrowLeft size={15} /> Back to site</Link>
    </header>
  );
}

export function HtmlSurveyFrame({ survey, className, onSubmitted }) {
  const src = directSrcFrom(survey.embed_html);
  useEffect(() => {
    if (src) return undefined;
    const onMessage = async (e) => {
      if (!e.data || e.data.type !== "vomarc-survey-submit" || !Array.isArray(e.data.answers)) return;
      try {
        await axios.post(`${API}/surveys/${survey.id}/responses`, { answers: e.data.answers.map((a) => ({ question_id: String(a.question_id || "").slice(0, 200), value: String(a.value ?? "").slice(0, 4000) })) });
        toast.success("Thank you! Your answers were recorded.");
        onSubmitted?.();
      } catch (err) {
        console.warn("Survey capture failed", err);
        toast.error("Could not save your answers. Please try again.");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [survey.id, src, onSubmitted]);

  if (src) {
    return <iframe title={survey.title} className={className} src={src} sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin" allow="fullscreen; camera; microphone; clipboard-read; clipboard-write" data-testid="survey-embed-iframe" />;
  }
  return (
    <>
      <iframe title={survey.title} className={className} srcDoc={withSurveyCapture(survey.embed_html)} sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-modals" data-testid="survey-embed-iframe" />
      <p className="survey-capture-note" data-testid="survey-capture-note">Answers submitted in this form are saved to VOMARC.</p>
    </>
  );
}

export function SurveysPage() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/surveys`).then((r) => setSurveys(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);


  return (
    <main className="vomarc-dark survey-page" data-testid="surveys-page">
      <SurveyHeader />
      <section className="survey-hero" data-testid="surveys-hero">
        <span className="eyebrow-dark"><ClipboardList size={13} /> VOMARC surveys</span>
        <h1>Tell us how you renovate.</h1>
        <p>Your answers shape what we build next. Nothing hidden, nothing lost.</p>
      </section>

      {loading && <p className="survey-empty" data-testid="surveys-loading">Loading surveys…</p>}
      {!loading && !surveys.length && (
        <p className="survey-empty" data-testid="surveys-empty">No surveys are live right now. Please check back soon.</p>
      )}

      <div className="survey-grid" data-testid="surveys-grid">
        {surveys.map((s) => (
          <article key={s.id} className="survey-card" data-testid={`survey-card-${s.id}`}>
            <span className={`survey-type-badge type-${s.survey_type}`} data-testid={`survey-type-${s.id}`}>{typeLabel[s.survey_type] || s.survey_type}</span>
            <h3 data-testid={`survey-title-${s.id}`}>{s.title}</h3>
            {s.description && <p data-testid={`survey-desc-${s.id}`}>{s.description}</p>}
            {s.survey_type === "native" && (
              <Link to={`/surveys/${s.id}`} className="primary-cta survey-action" data-testid={`survey-take-${s.id}`}>Take survey <ArrowUpRight size={16} /></Link>
            )}
            {s.survey_type === "link" && (
              <a href={s.external_url} target="_blank" rel="noreferrer" className="primary-cta survey-action" data-testid={`survey-open-link-${s.id}`}>Open survey <ExternalLink size={15} /></a>
            )}
            {s.survey_type === "html" && (
              <button className="primary-cta survey-action" onClick={() => setPreview(s)} data-testid={`survey-preview-${s.id}`}>Open preview <ArrowUpRight size={16} /></button>
            )}
          </article>
        ))}
      </div>

      {preview && (
        <div className="survey-modal-overlay" onClick={() => setPreview(null)} data-testid="survey-preview-overlay">
          <div className="survey-modal" onClick={(e) => e.stopPropagation()} data-testid="survey-preview-modal">
            <div className="survey-modal-bar">
              <strong>{preview.title}</strong>
              {directSrcFrom(preview.embed_html) && (
                <a className="product-embed-newtab" href={directSrcFrom(preview.embed_html)} target="_blank" rel="noreferrer" data-testid="survey-embed-newtab-link">Open in new tab</a>
              )}
              <button onClick={() => setPreview(null)} data-testid="survey-preview-close"><X size={18} /></button>
            </div>
            <HtmlSurveyFrame survey={preview} className="survey-embed-frame" />
          </div>
        </div>
      )}
    </main>
  );
}

function RatingInput({ value, onChange, testid }) {
  const num = Number(value) || 0;
  return (
    <div className="survey-rating" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          className={`survey-star ${n <= num ? "on" : ""}`}
          onClick={() => onChange(String(n))}
          data-testid={`${testid}-star-${n}`}
          aria-label={`${n} star`}
        >
          <Star size={22} fill={n <= num ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

export function SurveyTakePage() {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/surveys/${surveyId}`)
      .then((r) => setSurvey(r.data))
      .catch(() => setNotFound(true));
  }, [surveyId]);

  const setAnswer = (qid, value) => setAnswers((cur) => ({ ...cur, [qid]: value }));

  const toggleMulti = (qid, option) => {
    setAnswers((cur) => {
      const arr = Array.isArray(cur[qid]) ? [...cur[qid]] : [];
      const idx = arr.indexOf(option);
      if (idx >= 0) arr.splice(idx, 1); else arr.push(option);
      return { ...cur, [qid]: arr };
    });
  };

  const questions = useMemo(() => survey?.questions || [], [survey]);

  const submit = async (event) => {
    event.preventDefault();
    for (const q of questions) {
      const v = answers[q.id];
      const filled = Array.isArray(v) ? v.length > 0 : (v || "").toString().trim();
      if (q.required && !filled) {
        toast.error(`Please answer: ${q.label}`);
        return;
      }
    }
    const payload = {
      answers: questions.map((q) => ({
        question_id: q.id,
        value: Array.isArray(answers[q.id]) ? answers[q.id].join(", ") : (answers[q.id] || ""),
      })),
    };
    setSubmitting(true);
    try {
      await axios.post(`${API}/surveys/${surveyId}/responses`, payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your response");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <main className="vomarc-dark survey-page" data-testid="survey-take-notfound">
        <SurveyHeader />
        <p className="survey-empty">This survey is not available.</p>
      </main>
    );
  }

  if (!survey) {
    return (
      <main className="vomarc-dark survey-page" data-testid="survey-take-loading">
        <SurveyHeader />
        <p className="survey-empty">Loading survey…</p>
      </main>
    );
  }

  if (survey.survey_type === "html") {
    return (
      <main className="vomarc-dark survey-page" data-testid="survey-take-page">
        <SurveyHeader />
        <section className="survey-take-shell html">
          <h1 data-testid="survey-take-title">{survey.title}</h1>
          {survey.description && <p className="survey-take-desc" data-testid="survey-take-desc">{survey.description}</p>}
          <div className="survey-html-shell" data-testid="survey-html-shell"><HtmlSurveyFrame survey={survey} className="survey-embed-frame tall" /></div>
        </section>
      </main>
    );
  }

  if (survey.survey_type === "link") {
    return (
      <main className="vomarc-dark survey-page" data-testid="survey-take-page">
        <SurveyHeader />
        <section className="survey-take-shell">
          <h1 data-testid="survey-take-title">{survey.title}</h1>
          {survey.description && <p className="survey-take-desc" data-testid="survey-take-desc">{survey.description}</p>}
          <a href={survey.external_url} target="_blank" rel="noreferrer" className="primary-cta survey-action" data-testid="survey-take-open-link">Open survey <ExternalLink size={15} /></a>
        </section>
      </main>
    );
  }

  return (
    <main className="vomarc-dark survey-page" data-testid="survey-take-page">
      <SurveyHeader />
      <section className="survey-take-shell">
        {submitted ? (
          <div className="survey-thankyou" data-testid="survey-thankyou">
            <CheckCircle2 size={44} className="survey-thankyou-icon" />
            <h1>Thank you.</h1>
            <p>Your response has been recorded. We read every single one.</p>
            <Link to="/surveys" className="ghost-cta-dark" data-testid="survey-thankyou-back">Back to surveys</Link>
          </div>
        ) : (
          <form className="survey-form" onSubmit={submit} data-testid="survey-form">
            <h1 data-testid="survey-take-title">{survey.title}</h1>
            {survey.description && <p className="survey-take-desc" data-testid="survey-take-desc">{survey.description}</p>}

            {questions.map((q, i) => (
              <div key={q.id} className="survey-question" data-testid={`survey-question-${i}`}>
                <label className="survey-q-label">{q.label}{q.required && <span className="survey-req">*</span>}</label>

                {q.q_type === "short_text" && (
                  <Input value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`survey-input-${i}`} />
                )}
                {q.q_type === "email" && (
                  <Input type="email" value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`survey-input-${i}`} />
                )}
                {q.q_type === "long_text" && (
                  <Textarea value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} rows={4} data-testid={`survey-input-${i}`} />
                )}
                {q.q_type === "single_choice" && (
                  <div className="survey-options" data-testid={`survey-options-${i}`}>
                    {(q.options || []).map((opt, oi) => (
                      <label key={`${q.id}-${opt}`} className={`survey-option ${answers[q.id] === opt ? "chosen" : ""}`} data-testid={`survey-option-${i}-${oi}`}>
                        <input type="radio" name={q.id} checked={answers[q.id] === opt} onChange={() => setAnswer(q.id, opt)} />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                )}
                {q.q_type === "multi_choice" && (
                  <div className="survey-options" data-testid={`survey-options-${i}`}>
                    {(q.options || []).map((opt, oi) => {
                      const chosen = Array.isArray(answers[q.id]) && answers[q.id].includes(opt);
                      return (
                        <label key={`${q.id}-${opt}`} className={`survey-option ${chosen ? "chosen" : ""}`} data-testid={`survey-option-${i}-${oi}`}>
                          <input type="checkbox" checked={chosen} onChange={() => toggleMulti(q.id, opt)} />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {q.q_type === "rating" && (
                  <RatingInput value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} testid={`survey-rating-${i}`} />
                )}
              </div>
            ))}

            <Button type="submit" className="primary-cta full" disabled={submitting} data-testid="survey-submit-button">
              {submitting ? "Submitting…" : "Submit response"}
            </Button>
          </form>
        )}
      </section>
    </main>
  );
}
